import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI, questionsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { SUBJECT_COLORS, DIFFICULTY_COLORS, cn } from '../lib/utils';
import { toast } from 'sonner';
import {
    RefreshCw,
    CheckCircle,
    XCircle,
    ArrowRight,
    Clock,
    BookOpen,
    Lightbulb
} from 'lucide-react';
import Layout from '../components/Layout';

export default function Review() {
    const navigate = useNavigate();
    const [dueQuestions, setDueQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        fetchDueQuestions();
    }, []);

    const fetchDueQuestions = async () => {
        try {
            setLoading(true);
            const response = await analyticsAPI.getSpacedRepetition();
            setDueQuestions(response.data.due_questions);
        } catch (err) {
            console.error('Failed to fetch due questions:', err);
        } finally {
            setLoading(false);
        }
    };

    const currentQuestion = dueQuestions[currentIndex]?.question;

    const submitAnswer = async () => {
        if (!selectedAnswer || !currentQuestion) return;

        try {
            const response = await questionsAPI.submitAnswer(currentQuestion.id, {
                question_id: currentQuestion.id,
                selected_answer: selectedAnswer,
                time_spent_seconds: 0
            });
            setResult(response.data);
            setSubmitted(true);
            
            if (response.data.is_correct) {
                toast.success('Correct! This question will be scheduled for later review.');
            } else {
                toast.error('Incorrect. Review the explanation and try again soon.');
            }
        } catch (err) {
            console.error('Failed to submit answer:', err);
            toast.error('Failed to submit answer');
        }
    };

    const nextQuestion = () => {
        if (currentIndex < dueQuestions.length - 1) {
            setCurrentIndex(prev => prev + 1);
            setSelectedAnswer(null);
            setSubmitted(false);
            setResult(null);
        } else {
            toast.success('Review session complete!');
            navigate('/dashboard');
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-64" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="review-container">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                            Spaced Repetition Review
                        </h1>
                        <p className="text-slate-500 mt-1">
                            {dueQuestions.length > 0 
                                ? `${dueQuestions.length} questions due for review`
                                : 'No questions due for review'}
                        </p>
                    </div>
                    {dueQuestions.length > 0 && (
                        <Badge variant="outline" className="text-lg px-4 py-2">
                            {currentIndex + 1} / {dueQuestions.length}
                        </Badge>
                    )}
                </div>

                {dueQuestions.length > 0 && currentQuestion ? (
                    <div className="space-y-6">
                        {/* Question Header */}
                        <div className="flex items-center gap-3">
                            <Badge className={cn(
                                SUBJECT_COLORS[currentQuestion.subject]?.bg,
                                SUBJECT_COLORS[currentQuestion.subject]?.text,
                                SUBJECT_COLORS[currentQuestion.subject]?.border
                            )}>
                                {currentQuestion.subject}
                            </Badge>
                            <Badge className={cn(
                                DIFFICULTY_COLORS[currentQuestion.difficulty]?.bg,
                                DIFFICULTY_COLORS[currentQuestion.difficulty]?.text
                            )}>
                                {currentQuestion.difficulty}
                            </Badge>
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                <RefreshCw className="mr-1 h-3 w-3" />
                                Review
                            </Badge>
                        </div>

                        {/* Vignette */}
                        <Card className="border-slate-200 bg-slate-50">
                            <CardContent className="p-6">
                                <p className="text-slate-700 leading-relaxed" data-testid="review-vignette">
                                    {currentQuestion.vignette}
                                </p>
                            </CardContent>
                        </Card>

                        {/* Question */}
                        <Card className="border-slate-200">
                            <CardContent className="p-6">
                                <p className="text-lg font-medium text-slate-900 mb-6" data-testid="review-stem">
                                    {currentQuestion.question_stem}
                                </p>

                                {/* Options */}
                                <div className="space-y-3">
                                    {currentQuestion.options.map((option) => {
                                        const isSelected = selectedAnswer === option.letter;
                                        const isCorrect = submitted && option.letter === currentQuestion.correct_answer;
                                        const isWrong = submitted && isSelected && !isCorrect;

                                        return (
                                            <div
                                                key={option.letter}
                                                onClick={() => !submitted && setSelectedAnswer(option.letter)}
                                                className={cn(
                                                    "flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200",
                                                    !submitted && "hover:border-indigo-300 hover:bg-indigo-50/50",
                                                    isSelected && !submitted && "border-indigo-500 bg-indigo-50",
                                                    isCorrect && "border-emerald-500 bg-emerald-50",
                                                    isWrong && "border-red-500 bg-red-50",
                                                    submitted && !isCorrect && !isWrong && "opacity-60"
                                                )}
                                                data-testid={`review-option-${option.letter}`}
                                            >
                                                <div className={cn(
                                                    "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm",
                                                    !submitted && isSelected && "bg-indigo-600 text-white",
                                                    !submitted && !isSelected && "bg-slate-200 text-slate-600",
                                                    isCorrect && "bg-emerald-600 text-white",
                                                    isWrong && "bg-red-600 text-white"
                                                )}>
                                                    {submitted && isCorrect && <CheckCircle className="h-5 w-5" />}
                                                    {submitted && isWrong && <XCircle className="h-5 w-5" />}
                                                    {!submitted && option.letter}
                                                    {submitted && !isCorrect && !isWrong && option.letter}
                                                </div>
                                                <span className={cn(
                                                    "flex-1",
                                                    isCorrect && "text-emerald-700 font-medium",
                                                    isWrong && "text-red-700"
                                                )}>
                                                    {option.text}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Actions */}
                        <div className="flex justify-end gap-3">
                            {!submitted ? (
                                <Button 
                                    onClick={submitAnswer}
                                    disabled={!selectedAnswer}
                                    size="lg"
                                    data-testid="review-submit-btn"
                                >
                                    Submit Answer
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            ) : (
                                <Button 
                                    onClick={nextQuestion}
                                    size="lg"
                                    data-testid="review-next-btn"
                                >
                                    {currentIndex < dueQuestions.length - 1 ? 'Next Question' : 'Finish Review'}
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            )}
                        </div>

                        {/* Explanation */}
                        {submitted && (
                            <Card className={cn(
                                "border-2",
                                result?.is_correct ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"
                            )}>
                                <CardHeader className="pb-2">
                                    <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope' }}>
                                        {result?.is_correct ? (
                                            <>
                                                <CheckCircle className="h-5 w-5 text-emerald-600" />
                                                <span className="text-emerald-700">Correct!</span>
                                            </>
                                        ) : (
                                            <>
                                                <BookOpen className="h-5 w-5 text-amber-600" />
                                                <span className="text-amber-700">Review the Explanation</span>
                                            </>
                                        )}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className={cn(
                                        "leading-relaxed",
                                        result?.is_correct ? "text-emerald-800" : "text-amber-800"
                                    )} data-testid="review-explanation">
                                        {currentQuestion.explanation}
                                    </p>
                                    {!result?.is_correct && (
                                        <p className="mt-3 text-sm text-amber-700">
                                            The correct answer was <strong>{currentQuestion.correct_answer}</strong>.
                                            This question will appear again in your next review session.
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </div>
                ) : (
                    <Card className="border-slate-200 border-dashed">
                        <CardContent className="p-12">
                            <div className="text-center">
                                <CheckCircle className="h-16 w-16 mx-auto mb-4 text-emerald-400" />
                                <h3 className="text-lg font-semibold text-slate-700 mb-2" style={{ fontFamily: 'Manrope' }}>
                                    All Caught Up!
                                </h3>
                                <p className="text-slate-500 mb-6 max-w-md mx-auto">
                                    You have no questions due for review. Keep practicing to build your question bank!
                                </p>
                                <Button onClick={() => navigate('/practice')} data-testid="go-practice-from-review-btn">
                                    Practice New Questions
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
}
